import { Dispatch, ReactNode, SetStateAction, useMemo, useState } from 'react';
import BigNumber from 'bignumber.js';

import { DepositFormStateProvider } from '#/features/deposit/form-context';
import { BridgedToken } from '#/features/paraclear';

import createSafeContext from '#/utils/createSafeContext';
import { AsyncResult } from '#/utils/types';

import useIncreaseEthereumBridgeAllowance from './useIncreaseEthereumBridgeAllowance';
import useDepositToEthereumBridge from './useDepositToEthereumBridge';
import useFetchEthereumBridgeAllowance from './useFetchEthereumBridgeAllowance';

export interface DepositState {
  readonly status: 'idle' | 'busy';
  readonly allowance: BigNumber;
  readonly error: string;
}

export interface DepositView {
  readonly isBusy: boolean;
  readonly error: string;
}

const INITIAL_STATE: DepositState = {
  status: 'idle',
  allowance: new BigNumber(0),
  error: '',
};

export interface DepositActions {
  readonly fetchEthereumBridgeAllowance: (
    token: BridgedToken,
  ) => AsyncResult<void>;
  readonly increaseEthereumBridgeAllowance: (
    token: BridgedToken,
  ) => AsyncResult<void>;
  readonly depositToEthereumBridge: (token: BridgedToken) => AsyncResult<void>;
}

export const [useDepositState, DepositStateCtx] =
  createSafeContext<[DepositState, Dispatch<SetStateAction<DepositState>>]>(
    'DepositState',
  );
export const [useDepositView, DepositViewCtx] =
  createSafeContext<DepositView>('DepositView');
export const [useDepositActions, DepositActionsCtx] =
  createSafeContext<DepositActions>('DepositActions');

interface ProviderProps {
  readonly children: ReactNode;
}

export function DepositStateProvider(props: ProviderProps) {
  const state = useState(INITIAL_STATE);

  return (
    <DepositStateCtx value={state}>
      <DepositFormStateProvider>{props.children}</DepositFormStateProvider>
    </DepositStateCtx>
  );
}

export function DepositActionsProvider(props: ProviderProps) {
  const actions = useActions();

  return (
    <DepositActionsCtx value={actions}>{props.children}</DepositActionsCtx>
  );
}

export function DepositViewProvider(props: ProviderProps) {
  const [state] = useDepositState();
  const view = useMemo(() => createView(state), [state]);

  return <DepositViewCtx value={view}>{props.children}</DepositViewCtx>;
}

export function DepositProvider(props: ProviderProps) {
  return (
    <DepositStateProvider>
      <DepositViewProvider>
        <DepositActionsProvider>{props.children}</DepositActionsProvider>
      </DepositViewProvider>
    </DepositStateProvider>
  );
}

function useActions(): DepositActions {
  const fetchEthereumBridgeAllowance = useFetchEthereumBridgeAllowance();
  const increaseEthereumBridgeAllowance = useIncreaseEthereumBridgeAllowance();
  const depositToEthereumBridge = useDepositToEthereumBridge();

  return useMemo(
    () => ({
      fetchEthereumBridgeAllowance,
      increaseEthereumBridgeAllowance,
      depositToEthereumBridge,
    }),
    [
      depositToEthereumBridge,
      fetchEthereumBridgeAllowance,
      increaseEthereumBridgeAllowance,
    ],
  );
}

function createView(state: DepositState): DepositView {
  return {
    isBusy: state.status !== 'idle',
    error: state.error,
  };
}
